import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API_URL, formatTs } from './utils';

const STATS_RANGE_OPTIONS = [
  { value: 'day', label: 'Past day' },
  { value: 'week', label: 'Past week' },
  { value: 'month', label: 'Past month' },
  { value: 'six_months', label: 'Past 6 months' },
  { value: 'all', label: 'All time' },
];

/** Normalize per-user breakdown rows (snake_case / camelCase / PascalCase / JSON string). */
function normalizeByUserRows(raw) {
  if (raw == null) return [];
  if (typeof raw === 'string') {
    try {
      return normalizeByUserRows(JSON.parse(raw));
    } catch {
      return [];
    }
  }
  if (!Array.isArray(raw)) {
    if (typeof raw === 'object') {
      return normalizeByUserRows([raw]);
    }
    return [];
  }
  return raw
    .map((r) => {
      if (r == null) return null;
      if (typeof r !== 'object') return null;
      const user_id = String(
        r.user_id ?? r.userId ?? r.UserId ?? '',
      );
      const username = String(
        r.username ?? r.userName ?? r.UserName ?? '(unknown)',
      );
      const user_messages =
        Number(
          r.user_messages ??
            r.userMessages ??
            r.UserMessages ??
            r.user_message_count ??
            r.userMessageCount,
        ) || 0;
      const assistant_messages =
        Number(
          r.assistant_messages ??
            r.assistantMessages ??
            r.AssistantMessages ??
            r.assistant_message_count ??
            r.assistantMessageCount,
        ) || 0;
      return { user_id, username, user_messages, assistant_messages };
    })
    .filter(Boolean);
}

/** Read breakdown from a bucket object (hour or volume). */
function userBreakdownFromBucket(b) {
  if (b == null) return [];
  return normalizeByUserRows(
    b.by_user ?? b.byUser ?? b.users ?? b.Users,
  );
}

/** If by_user is missing but users is present, align so tooltips can read either key. */
function ensureByUserOnBuckets(buckets) {
  if (!Array.isArray(buckets)) return;
  for (const b of buckets) {
    if (b == null || typeof b !== 'object') continue;
    const primary = b.by_user ?? b.byUser;
    const alt = b.users ?? b.Users;
    const hasPrimary = Array.isArray(primary) && primary.length > 0;
    const hasAlt = Array.isArray(alt) && alt.length > 0;
    if (!hasPrimary && hasAlt) {
      b.by_user = alt;
    }
  }
}

/**
 * Deep-clone API JSON and normalise camelCase keys to snake_case where
 * older payloads used the camelCase form. Bucket arrays from the current
 * server are passed through untouched so nested fields like `by_user`
 * are preserved.
 */
function coerceAdminStats(data) {
  if (data == null || typeof data !== 'object') return data;
  let next;
  try {
    next = JSON.parse(JSON.stringify(data));
  } catch {
    next = { ...data };
  }

  if (!next.activity_by_hour_utc && Array.isArray(next.activityByHourUtc)) {
    next.activity_by_hour_utc = next.activityByHourUtc;
  }
  if (!next.activity_over_time && next.activityOverTime) {
    next.activity_over_time = next.activityOverTime;
  }

  const hours = next.activity_by_hour_utc;
  if (Array.isArray(hours) && hours.length === 24 && hours.every((x) => typeof x === 'number')) {
    next.activity_by_hour_utc = hours.map((total, hour) => ({
      hour,
      total: Number(total) || 0,
      by_user: [],
      users: [],
    }));
  }

  const over = next.activity_over_time ?? next.activityOverTime;
  const hasBuckets = over && Array.isArray(over.buckets);
  const legacyDay = next.activity_by_day ?? next.activityByDay;
  if (!hasBuckets && Array.isArray(legacyDay) && legacyDay.length > 0) {
    next.activity_over_time = {
      granularity: 'day',
      chart_title: 'Volume over time',
      buckets: legacyDay.map((d) => ({
        key: String(d.key ?? d.date ?? ''),
        label: String(d.label ?? d.date ?? d.key ?? ''),
        user_messages: Number(d.user_messages) || 0,
        assistant_messages: Number(d.assistant_messages) || 0,
        conversations_started: Number(d.conversations_started) || 0,
        by_user: Array.isArray(d.by_user) ? d.by_user : [],
        users: Array.isArray(d.users) ? d.users : [],
      })),
    };
  }

  ensureByUserOnBuckets(next.activity_by_hour_utc);
  ensureByUserOnBuckets(next.activity_over_time?.buckets);

  return next;
}

function StatsChartTooltip({ tip }) {
  if (!tip) return null;
  const { x, y, title, summaryLines, rows, breakdownMode } = tip;
  const safeRows = Array.isArray(rows)
    ? rows.map((r) => ({
        user_id: r.user_id ?? r.userId ?? '',
        username: r.username ?? '(unknown)',
        user_messages:
          Number(
            r.user_messages ??
              r.userMessages ??
              r.user_message_count ??
              r.userMessageCount,
          ) || 0,
        assistant_messages:
          Number(
            r.assistant_messages ??
              r.assistantMessages ??
              r.assistant_message_count ??
              r.assistantMessageCount,
          ) || 0,
      }))
    : [];
  const isHourOnly = breakdownMode === 'hour';
  const colSpan = isHourOnly ? 2 : 3;

  return (
    <div
      className="AdminPortal-chartTooltip"
      style={{
        position: 'fixed',
        left: x + 12,
        top: y + 12,
        zIndex: 10000,
        pointerEvents: 'none',
      }}
      role="tooltip"
    >
      <div className="AdminPortal-chartTooltip-title">{title}</div>
      {summaryLines?.map((line, i) => (
        <div key={i} className="AdminPortal-chartTooltip-summary">
          {line}
        </div>
      ))}
      <div className="AdminPortal-chartTooltip-byline">
        Counts per user in this bucket (not message text).
      </div>
      <div className="AdminPortal-chartTooltip-tableWrap">
        <table className="AdminPortal-chartTooltip-table">
          <thead>
            <tr>
              <th scope="col">User</th>
              {isHourOnly ? (
                <th scope="col">User messages</th>
              ) : (
                <>
                  <th scope="col">User-sent</th>
                  <th scope="col">Assistant</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {!safeRows.length ? (
              <tr>
                <td colSpan={colSpan} className="AdminPortal-chartTooltip-empty">
                  No per-user breakdown for this bucket
                </td>
              </tr>
            ) : (
              safeRows.map((r, idx) => (
                <tr key={r.user_id || `${r.username}-${idx}`}>
                  <td>{r.username}</td>
                  {isHourOnly ? (
                    <td>{r.user_messages}</td>
                  ) : (
                    <>
                      <td>{r.user_messages}</td>
                      <td>{r.assistant_messages}</td>
                    </>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export const StatsTab = ({ visible, authHeaders }) => {
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState(null);
  const [statsRange, setStatsRange] = useState('month');
  const [chartTooltip, setChartTooltip] = useState(null);

  const statsFetchGen = useRef(0);
  const statsAbortRef = useRef(null);

  const fetchStats = useCallback(async (range) => {
    statsAbortRef.current?.abort();
    const controller = new AbortController();
    statsAbortRef.current = controller;
    const gen = ++statsFetchGen.current;

    setStatsError(null);
    setStatsLoading(true);
    const base = API_URL.replace(/\/$/, '');
    const url = `${base}/api/admin/statistics?range=${encodeURIComponent(range)}&_cb=${Date.now()}`;
    try {
      const res = await axios.get(url, {
        signal: controller.signal,
        headers: {
          ...authHeaders(),
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
      });
      if (gen !== statsFetchGen.current) return;
      setStats(coerceAdminStats(res.data));
    } catch (err) {
      const canceled =
        err.code === 'ERR_CANCELED' ||
        err.name === 'CanceledError' ||
        (typeof axios.isCancel === 'function' && axios.isCancel(err));
      if (canceled) return;
      if (gen !== statsFetchGen.current) return;
      const msg =
        err.response?.data?.error ||
        err.message ||
        'Failed to load statistics';
      setStatsError(msg);
      setStats(null);
    } finally {
      if (gen === statsFetchGen.current) {
        setStatsLoading(false);
      }
    }
  }, [authHeaders]);

  useEffect(() => {
    if (!visible) return;
    fetchStats(statsRange);
  }, [visible, statsRange, fetchStats]);

  useEffect(() => {
    return () => {
      statsAbortRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    setChartTooltip(null);
  }, [stats, statsRange]);

  const loadStats = useCallback(() => fetchStats(statsRange), [fetchStats, statsRange]);

  const hourBuckets = stats?.activity_by_hour_utc || [];
  const hourTotals = hourBuckets.map((b) =>
    Number.isFinite(b?.total) ? b.total : 0,
  );
  const hourMax = hourTotals.length === 24 ? Math.max(1, ...hourTotals) : 1;

  const timeBuckets = stats?.activity_over_time?.buckets || [];
  const timeMax = Math.max(
    1,
    ...timeBuckets.map((b) => {
      const u = Number.isFinite(b?.user_messages) ? b.user_messages : 0;
      const a = Number.isFinite(b?.assistant_messages) ? b.assistant_messages : 0;
      return u + a;
    }),
  );

  const moveChartTooltip = (e) => {
    setChartTooltip((t) =>
      t ? { ...t, x: e.clientX, y: e.clientY } : t,
    );
  };

  return (
    <>
      {statsError && (
        <div className="AdminPortal-banner AdminPortal-banner--error" role="alert">
          {statsError}
        </div>
      )}

      <div className="AdminPortal-toolbar AdminPortal-toolbar--split">
        <div className="AdminPortal-statsToolbarLeft">
          <label className="AdminPortal-rangeField">
            <span className="AdminPortal-rangeFieldLabel">Usage period</span>
            <select
              className="AdminPortal-select AdminPortal-rangeSelect"
              value={statsRange}
              onChange={(e) => setStatsRange(e.target.value)}
              aria-label="Filter statistics by time range"
            >
              {STATS_RANGE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <p className="AdminPortal-statsMeta">
            {stats?.generated_at && (
              <>
                Snapshot:{' '}
                <time dateTime={stats.generated_at}>{formatTs(stats.generated_at)}</time>
                {stats.range != null && (
                  <>
                    {' · '}
                    API window: <strong>{stats.range_label ?? stats.range}</strong>
                  </>
                )}
              </>
            )}
          </p>
        </div>
        <button
          type="button"
          className="ChatButton"
          onClick={loadStats}
          disabled={statsLoading}
        >
          Refresh
        </button>
      </div>

      {statsLoading && !stats && (
        <p className="AdminPortal-intro">Loading statistics…</p>
      )}

      {stats && (
        <>
          <p className="AdminPortal-statsNote">{stats.timezone_note}</p>

          <section className="AdminPortal-section" aria-labelledby="stats-overview-heading">
            <h2 id="stats-overview-heading" className="AdminPortal-sectionTitle">
              Overview
            </h2>
            <p className="AdminPortal-sectionLead AdminPortal-rangePill">
              Selected window: <strong>{stats.range_label}</strong>
              {stats.range_start && (
                <>
                  {' '}
                  ({formatTs(stats.range_start)} → {formatTs(stats.range_end)})
                </>
              )}
            </p>
            <div className="AdminPortal-statGrid">
              <div className="AdminPortal-statCard">
                <span className="AdminPortal-statLabel">Registered users</span>
                <span className="AdminPortal-statValue">{stats.totals.registered_users}</span>
                <span className="AdminPortal-statHint">All accounts</span>
              </div>
              <div className="AdminPortal-statCard">
                <span className="AdminPortal-statLabel">Conversations (in period)</span>
                <span className="AdminPortal-statValue">{stats.totals.total_conversations}</span>
              </div>
              <div className="AdminPortal-statCard">
                <span className="AdminPortal-statLabel">Messages (in period)</span>
                <span className="AdminPortal-statValue">{stats.totals.total_messages}</span>
              </div>
              <div className="AdminPortal-statCard">
                <span className="AdminPortal-statLabel">User / assistant (period)</span>
                <span className="AdminPortal-statValue">
                  {stats.totals.user_messages} / {stats.totals.assistant_messages}
                </span>
              </div>
              <div className="AdminPortal-statCard">
                <span className="AdminPortal-statLabel">Active in period</span>
                <span className="AdminPortal-statValue">
                  {stats.totals.active_users_in_range}
                </span>
                <span className="AdminPortal-statHint">≥1 user message in window</span>
              </div>
              <div className="AdminPortal-statCard">
                <span className="AdminPortal-statLabel">Quiet in period</span>
                <span className="AdminPortal-statValue">
                  {stats.totals.users_quiet_in_range}
                </span>
                <span className="AdminPortal-statHint">No user msgs in window</span>
              </div>
              <div className="AdminPortal-statCard">
                <span className="AdminPortal-statLabel">Active (last 7 days)</span>
                <span className="AdminPortal-statValue">
                  {stats.totals.active_users_last_7_days}
                </span>
                <span className="AdminPortal-statHint">Rolling; ignores period filter</span>
              </div>
              <div className="AdminPortal-statCard">
                <span className="AdminPortal-statLabel">Never messaged (ever)</span>
                <span className="AdminPortal-statValue">
                  {stats.totals.inactive_users_no_user_messages_ever}
                </span>
                <span className="AdminPortal-statHint">Registered, 0 user msgs total</span>
              </div>
              <div className="AdminPortal-statCard">
                <span className="AdminPortal-statLabel">Avg conv. / registered</span>
                <span className="AdminPortal-statValue">
                  {stats.totals.avg_conversations_per_user}
                </span>
              </div>
              <div className="AdminPortal-statCard">
                <span className="AdminPortal-statLabel">Avg user msgs (chatters)</span>
                <span className="AdminPortal-statValue">
                  {stats.totals.avg_user_messages_per_messaging_user}
                </span>
                <span className="AdminPortal-statHint">Among users who messaged in period</span>
              </div>
            </div>
          </section>

          <section className="AdminPortal-section" aria-labelledby="stats-hour-heading">
            <h2 id="stats-hour-heading" className="AdminPortal-sectionTitle">
              When people message (UTC hour of day)
            </h2>
            <p className="AdminPortal-sectionLead">
              Counts only <strong>user</strong> messages in <strong>{stats.range_label}</strong>, grouped
              by clock hour (0–23 UTC). Hover a bar for per-user split.
            </p>
            <div
              className="AdminPortal-hourChart"
              role="img"
              aria-label="User messages by hour UTC"
              onPointerLeave={() => setChartTooltip(null)}
            >
              {hourBuckets.map((bucket) => (
                <div
                  key={bucket.hour}
                  className="AdminPortal-hourBarWrap AdminPortal-barHit"
                  onPointerEnter={(e) => {
                    setChartTooltip({
                      x: e.clientX,
                      y: e.clientY,
                      breakdownMode: 'hour',
                      title: `${bucket.hour ?? '?' }:00–${bucket.hour ?? '?' }:59 UTC`,
                      summaryLines: [
                        `${Number.isFinite(bucket.total) ? bucket.total : 0} user message${bucket.total === 1 ? '' : 's'} (in selected period)`,
                      ],
                      rows: userBreakdownFromBucket(bucket),
                    });
                  }}
                  onPointerMove={moveChartTooltip}
                >
                  <div
                    className="AdminPortal-hourBar"
                    style={{
                      height: `${((Number.isFinite(bucket.total) ? bucket.total : 0) / hourMax) * 100}%`,
                    }}
                  />
                  <span className="AdminPortal-hourLabel">{bucket.hour ?? '—'}</span>
                </div>
              ))}
            </div>
          </section>

          <section className="AdminPortal-section" aria-labelledby="stats-day-heading">
            <h2 id="stats-day-heading" className="AdminPortal-sectionTitle">
              {stats.activity_over_time?.chart_title ?? 'Volume over time'}
            </h2>
            <p className="AdminPortal-sectionLead">
              Bar height = user + assistant messages in each bucket. Tooltip lists per-user user vs assistant
              counts and new chats started in that bucket.
            </p>
            <div
              className="AdminPortal-dayChart"
              role="img"
              aria-label="Messages over selected period"
              onPointerLeave={() => setChartTooltip(null)}
            >
              {timeBuckets.map((b) => {
                const vol = b.user_messages + b.assistant_messages;
                const h = (vol / timeMax) * 100;
                return (
                  <div
                    key={b.key}
                    className="AdminPortal-dayCol AdminPortal-barHit"
                    onPointerEnter={(e) => {
                      setChartTooltip({
                        x: e.clientX,
                        y: e.clientY,
                        breakdownMode: 'volume',
                        title: b.label,
                        summaryLines: [
                          `${vol} message${vol === 1 ? '' : 's'} (user ${b.user_messages}, assistant ${b.assistant_messages})`,
                          `New conversations started: ${b.conversations_started}`,
                        ],
                        rows: userBreakdownFromBucket(b),
                      });
                    }}
                    onPointerMove={moveChartTooltip}
                  >
                    <div className="AdminPortal-dayBarWrap">
                      <div
                        className="AdminPortal-dayBar"
                        style={{ height: `${h}%` }}
                      />
                    </div>
                    <span className="AdminPortal-dayLabel">{b.label}</span>
                  </div>
                );
              })}
            </div>
            <div className="AdminPortal-dayLegend">
              <span>
                <i className="AdminPortal-legendSwatch AdminPortal-legendSwatch--msg" /> Volume
              </span>
              <span>Hover a bar for per-user breakdown</span>
            </div>
          </section>

          <section className="AdminPortal-section" aria-labelledby="stats-top-heading">
            <h2 id="stats-top-heading" className="AdminPortal-sectionTitle">
              Top users by activity
            </h2>
            <p className="AdminPortal-sectionLead">
              Ranked by user messages in <strong>{stats.range_label}</strong> (chats created in period only).
            </p>
            <div className="AdminPortal-tableWrap">
              <table className="AdminPortal-table">
                <thead>
                  <tr>
                    <th scope="col">Username</th>
                    <th scope="col">Role</th>
                    <th scope="col">Chats</th>
                    <th scope="col">User msgs</th>
                    <th scope="col">Assistant msgs</th>
                    <th scope="col">Last activity</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.top_users.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="AdminPortal-tableEmpty">
                        No usage data yet.
                      </td>
                    </tr>
                  ) : (
                    stats.top_users.map((u) => (
                      <tr key={u.user_id}>
                        <td>
                          <span className="AdminPortal-username">{u.username}</span>
                          <span className="AdminPortal-mono AdminPortal-subId">{u.user_id}</span>
                        </td>
                        <td>{u.role}</td>
                        <td>{u.conversation_count}</td>
                        <td>{u.user_message_count}</td>
                        <td>{u.assistant_message_count}</td>
                        <td>{formatTs(u.last_activity_at)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}

      <StatsChartTooltip tip={chartTooltip} />
    </>
  );
};
